import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, HelpCircle } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STORAGE_KEY = "elh_support_v2";

/**
 * Floating customer-support chatbot. Available on every page (mounted at the
 * root of App.js). Anonymous-friendly, sends a session_id but no auth.
 */
export default function SupportChatWidget() {
    const { pathname } = useLocation();
    // Inside the authenticated platform, shrink to an unobtrusive help icon.
    const inApp = pathname.startsWith("/app");
    const [open, setOpen] = useState(false);
    const [messages, setMessages] = useState(() => {
        if (typeof window === "undefined") return [seedGreeting()];
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) return JSON.parse(raw);
        } catch {
            /* noop */
        }
        return [seedGreeting()];
    });
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const scrollRef = useRef(null);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(messages.slice(-30)));
        } catch {
            /* noop */
        }
    }, [messages]);

    useEffect(() => {
        if (open && scrollRef.current) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    }, [open, messages, busy]);

    const send = async (e) => {
        e?.preventDefault();
        const text = input.trim();
        if (!text || busy) return;
        const userMsg = { role: "user", content: text };
        const next = [...messages, userMsg];
        setMessages(next);
        setInput("");
        setBusy(true);
        setError("");
        try {
            // Send only the last 12 turns to keep payload light
            const history = next.slice(-12);
            const sessionId = getOrInitSessionId();
            const { data } = await axios.post(`${BACKEND}/api/support/chat`, {
                message: text,
                history,
                session_id: sessionId,
            });
            setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
        } catch (e2) {
            const d = e2?.response?.data?.detail;
            const msg = typeof d === "string" ? d : d?.message || "Something went wrong.";
            setError(msg);
            setMessages((m) => [
                ...m,
                {
                    role: "assistant",
                    content:
                        msg +
                        " You can also email altay@eurolawhub.com and the team will get back to you.",
                },
            ]);
        } finally {
            setBusy(false);
        }
    };

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className={
                    inApp
                        ? `fixed bottom-24 right-4 lg:bottom-5 lg:right-5 z-40 w-9 h-9 rounded-full border shadow-sm flex items-center justify-center transition-colors ${
                              open
                                  ? "bg-[#0D1B3E] text-[#F5F0E8] border-[#0D1B3E]"
                                  : "bg-white/90 text-[#0D1B3E]/60 border-[#0D1B3E]/20 hover:text-[#0D1B3E] hover:border-[#0D1B3E]/45"
                          }`
                        : `fixed bottom-24 right-5 lg:bottom-24 lg:right-7 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all ${
                              open
                                  ? "bg-[#0D1B3E] text-[#F5F0E8]"
                                  : "bg-[#0D1B3E] text-[#C9A84C] hover:scale-105"
                          }`
                }
                aria-label={open ? "Close support chat" : "Open support chat"}
                data-testid="support-widget-toggle"
            >
                {open ? (
                    <X size={inApp ? 15 : 22} />
                ) : inApp ? (
                    <HelpCircle size={17} />
                ) : (
                    <MessageCircle size={22} />
                )}
            </button>

            {open && (
                <div
                    className={
                        inApp
                            ? "fixed bottom-36 right-4 lg:bottom-16 lg:right-5 z-40 w-[calc(100vw-2rem)] sm:w-[360px] max-w-[380px] h-[460px] max-h-[calc(100vh-12rem)] bg-white border border-[#0D1B3E]/15 shadow-2xl flex flex-col"
                            : "fixed bottom-44 right-5 lg:bottom-44 lg:right-7 z-50 w-[calc(100vw-2.5rem)] sm:w-[380px] max-w-[400px] h-[560px] max-h-[calc(100vh-14rem)] bg-white border border-[#0D1B3E]/15 shadow-2xl flex flex-col"
                    }
                    data-testid="support-widget-panel"
                >
                    <header className="px-5 py-4 bg-[#0D1B3E] text-[#F5F0E8]">
                        <div className="label-eyebrow" style={{ color: "#C9A84C" }}>
                            Support
                        </div>
                        <div className="mt-0.5 font-display text-lg tracking-tight">
                            EuroLaw Hub help desk
                        </div>
                        <div className="mt-1 text-[11px] text-[#F5F0E8]/65">
                            Product questions only. We do not give legal advice on a matter.
                        </div>
                    </header>

                    <div
                        ref={scrollRef}
                        className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F5F0E8]"
                        data-testid="support-widget-messages"
                    >
                        {messages.map((m, i) => (
                            <Bubble key={i} role={m.role} content={m.content} />
                        ))}
                        {busy && (
                            <div className="flex items-center gap-2 text-xs text-[#0D1B3E]/55">
                                <Loader2 className="animate-spin" size={14} />
                                <span>Typing…</span>
                            </div>
                        )}
                    </div>

                    <form
                        onSubmit={send}
                        className="border-t border-[#0D1B3E]/10 px-3 py-3 flex items-center gap-2 bg-white"
                    >
                        <input
                            className="field flex-1 text-sm"
                            placeholder="Ask about pricing, modules, account…"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            disabled={busy}
                            data-testid="support-widget-input"
                        />
                        <button
                            type="submit"
                            className="w-10 h-10 grid place-items-center bg-[#0D1B3E] text-[#F5F0E8] disabled:opacity-40"
                            disabled={busy || !input.trim()}
                            aria-label="Send"
                            data-testid="support-widget-send"
                        >
                            <Send size={16} />
                        </button>
                    </form>
                </div>
            )}
        </>
    );
}

function Bubble({ role, content }) {
    const isUser = role === "user";
    return (
        <div
            className={`flex ${isUser ? "justify-end" : "justify-start"}`}
            data-testid={`support-bubble-${role}`}
        >
            <div
                className={`max-w-[85%] px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                    isUser
                        ? "bg-[#0D1B3E] text-[#F5F0E8]"
                        : "bg-white border border-[#0D1B3E]/15 text-[#0D1B3E]/90"
                }`}
            >
                {content}
            </div>
        </div>
    );
}

function seedGreeting() {
    return {
        role: "assistant",
        content:
            "Hi! I'm the EuroLaw Hub assistant. I can help with questions about features, pricing, billing, and using the platform. How can I help?",
    };
}

function getOrInitSessionId() {
    try {
        const key = "elh_support_session";
        let v = localStorage.getItem(key);
        if (!v) {
            v = "s_" + Math.random().toString(36).slice(2, 12);
            localStorage.setItem(key, v);
        }
        return v;
    } catch {
        return "s_anon";
    }
}
